import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { ButtonContainer, Container, FeedbackHeader } from './FeedbackModal.styled';
import { XMark } from 'assets/icons/Icons';
import Modal from 'components/Modal/Modal';
import { Button } from 'components/v2/Buttons/Button/Button';
import { LinkButton } from 'components/v2/Buttons/LinkButton/LinkButton';
import { TextArea } from 'components/v2/Inputs/TextArea/TextArea';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { usePatient } from 'hooks/usePatient/usePatient';
import theme from 'kb-shared/theme';
import KBContacts from 'kb-shared/utilities/kindbody_contacts';
import { showErrorToast, showSuccessToast } from 'utilities/notificationUtils';

import { CREATE_PATIENT_FEEDBACK } from './FeedbackModal.graphql';

export const FeedbackModal = ({
  title,
  open,
  close,
  id,
  mobileHeight
}: {
  title: string;
  open: boolean;
  close: () => void;
  id: string;
  mobileHeight?: string;
}) => {
  const { patient } = usePatient();
  const [value, setValue] = useState('');

  const [createFeedback, { loading }] = useMutation(CREATE_PATIENT_FEEDBACK, {
    onCompleted: () => {
      setValue('');
      showSuccessToast('Thank you for your feedback!');
      close();
    },
    onError: () => {
      showErrorToast(
        `Failed to send feedback. Please wait then try again, or contact ${KBContacts.navigatorEmail}`
      );
    }
  });

  const handleSend = () => {
    if (loading) return;
    if (!value.trim()) return;

    createFeedback({
      variables: {
        marketId: Number(patient?.lab?.id),
        employerId: patient?.patientMembership?.employer
          ? Number(patient?.patientMembership?.employer)
          : null,
        text: value
      }
    });
  };

  return (
    <Modal
      label={title}
      open={open}
      onRequestClose={close}
      maxWidth="800px"
      mobileHeight={mobileHeight}
    >
      <Container>
        <FeedbackHeader>
          <Heading noMargin tag="h2">
            {title}
          </Heading>
          <LinkButton leftIcon={<XMark type="solid" />} size="sm" onClick={close} />
        </FeedbackHeader>

        <Text size={'md'} color={theme.colors.neutral.primaryNavy} fontStyle="regular">
          We value your input! Please share your thoughts below to help us improve your experience.{' '}
        </Text>
        <Text size={'sm'} color={theme.colors.neutral.navy} fontStyle="regular">
          Message
        </Text>
        <TextArea
          height="290px"
          id={`feedback-textarea-${id}`}
          placeholder="Start typing..."
          value={value}
          onChange={e => {
            setValue(e.currentTarget.value);
          }}
        />
        <ButtonContainer>
          <Button fullWidth category="secondary" size="lg" label="Cancel" onClick={close} />
          <Button
            fullWidth
            category="primary"
            size="lg"
            label="Send"
            isDisabled={!value.trim() || loading}
            onClick={handleSend}
          />
        </ButtonContainer>
      </Container>
    </Modal>
  );
};
